/**
 * Entry point du loader (sdk.halmya.com/loader.js).
 *
 * Responsabilités :
 *  1. Lire `data-key` et la base URL du SDK depuis le `<script>` tag
 *  2. Initialiser `window.halmya` en mode queue (rejouée par le core plus tard)
 *  3. Récupérer la config publique du workspace
 *  4. Attendre le consentement explicite (strict opt-in V1)
 *  5. Lazy-importer `core.js` et lui passer le bootstrap context
 *
 * Garantie absolue : aucune exception ne doit jamais remonter au site client.
 * Toute l'exécution est wrappée dans un try/catch.
 */

import { fetchConfig, deriveApiBaseUrl } from "./config";
import { waitForConsent } from "./consent";
import { loadCore } from "./core-loader";
import { ensureQueue } from "./queue";
import { readScriptTag } from "./script-tag";

(function bootstrap(): void {
  try {
    const tagInfo = readScriptTag();
    if (tagInfo === null) {
      // eslint-disable-next-line no-console
      console.warn("[halmya] loader could not find its <script> tag or data-key");
      return;
    }

    const apiBaseUrl = deriveApiBaseUrl(tagInfo.sdkBaseUrl);
    if (apiBaseUrl === null) {
      // eslint-disable-next-line no-console
      console.warn("[halmya] could not derive API base URL from SDK URL", tagInfo.sdkBaseUrl);
      return;
    }

    const queue = ensureQueue();

    // Fetch de la config en parallèle de l'attente du consent.
    const configPromise = fetchConfig(apiBaseUrl, tagInfo.publicKey);

    waitForConsent(queue, () => {
      configPromise
        .then((config) => {
          if (config === null) {
            // eslint-disable-next-line no-console
            console.warn("[halmya] workspace config unavailable, aborting");
            return;
          }

          return loadCore({
            publicKey: tagInfo.publicKey,
            apiBaseUrl: config.apiDomain ?? apiBaseUrl,
            sdkBaseUrl: tagInfo.sdkBaseUrl,
            config,
            queue,
          });
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn("[halmya] bootstrap failed:", err);
        });
    });
  } catch (err) {
    // Filet de sécurité ultime : n'importe quelle erreur de plomberie ici
    // ne doit pas casser la page du client.
    // eslint-disable-next-line no-console
    console.warn("[halmya] loader crashed:", err);
  }
})();
